import { ChangeDetectorRef, Component, HostBinding, OnChanges, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { BaseRenderComponent } from 'cms/workspace/workspace-generic-classes/base-render.component';
import { RenderEvent } from 'render/render-events/render-events.model';
import { RenderEventsService } from 'render/render-events/render-events.service';
import { RenderByDeviceService } from 'render/services/base-render-by-device/render-by-device.service';
import { RenderConfigurationService } from 'render/services/render-configuration.service';
import { debounceTime, takeUntil, timer } from 'rxjs';
import { AmandaOptions, AssetsSharedService } from 'shared/services/assets-shared.service';

import { PieceNewsletterFieldsBirthdate, PieceNewsletterLinkedCategory, PieceNewsletterSettings } from './models/piece-newsletter.models';
import { styleUrls, templateUrl } from './piece-newsletter-render.component.paths';


type Status = 'loading' | 'success' | 'failure' | 'none';

@Component({
  selector: 'piece-newsletter-render',
  templateUrl,
  styleUrls
})
export class PieceNewsletterRenderComponent
  extends BaseRenderComponent<PieceNewsletterSettings>
  implements OnInit, OnChanges {

  public form!: FormGroup<{
    email: FormControl<string | null>;
    agree: FormControl<boolean | null>;
    linkedCategories: FormArray<FormControl<boolean | null>>;
    birthdate?: FormGroup<{
      day: FormControl<number | null>;
      month: FormControl<number | null>;
      year?: FormControl<number | null>;
    }>;
  }>;

  public showUnsuscribeForm: boolean = false;
  public showUnsuscribeMessage: boolean = false;
  /** Controls whether the user has tried to submit the form */
  public isFormSubmitted: boolean = false;
  /** Controls whether we have actually sent the form event */
  public isFormSent = false;
  /** Controls whether we have actually sent the form event and it's still loading */
  public isLoading: boolean = false;

  constructor(
    protected readonly renderConfigurationService: RenderConfigurationService,
    private readonly assetsSharedService: AssetsSharedService,
    private readonly renderEventsService: RenderEventsService,
    private readonly renderByDeviceService: RenderByDeviceService,
    private readonly cd: ChangeDetectorRef,
  ) {
    super();
  }

  @HostBinding('class.disable-interaction')
  public get disableInteraction(): boolean {
    return this.type === 'workspace';
  }

  @HostBinding('class.email-invalid')
  public get emailInvalid(): boolean {
    return this.isFormSubmitted && this.form.controls.email.invalid;
  }

  public get legalConditionsInvalid(): boolean {
    return this.isFormSubmitted && this.form.controls.agree.invalid;
  }

  @HostBinding('style.--input-box-color')
  public get inputBoxColor(): string {
    if (this.piece?.settings?.styles?.inputBoxColor) {
      return this.piece?.settings?.styles?.inputBoxColor;
    }
    return '#000';
  }

  @HostBinding('style.--checkbox-icon')
  public get checkboxIcon(): string {
    const color = encodeURIComponent(this.getInputColor);
    return `url("data:image/svg+xml,%3Csvg width='24' height='19' viewBox='0 0 24 19' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 8.34632L7.9703 16.6235L22.5644 1.1582' stroke='${color}' stroke-width='2' stroke-miterlimit='10'/%3E%3C/svg%3E%0A")`;
  }

  @HostBinding('style.--background-color')
  public get backgroundColor(): string {
    if (this.piece?.settings?.styles?.bgColor) {
      return this.piece?.settings?.styles?.bgColor;
    }
    return '#0000';
  }

  @HostBinding('style.--status-icon')
  public get status(): Status {
    if (this.isLoading) {
      return 'loading';
    }
    if (this.emailInvalid) {
      return 'failure';
    }

    if (this.isFormSent) {
      return 'success';
    }

    return 'none';
  }

  @HostBinding('style.--placeholder-color')
  public get placeholderColor(): string {
    return this.settings.general?.placeholderStyles?.inputPlaceholderColor ?? '#000';
  }

  @HostBinding('style.--input-color')
  public get getInputColor(): string {
    const inputTextColor = this.settings.general?.placeholderStyles?.inputTextColor;
    return inputTextColor
      ? this.toUnit(inputTextColor) as string
      : '#000';
  }


  public get inputStyle(): Record<string, string | undefined> {
    const inputColor = this.getInputColor;

    return {
      color: inputColor,
      'background-color': '#ffffff00',
      'font-size': this.toUnit(this.settings.general?.placeholderStyles?.inputTextSize),
      'padding-block-start': this.toUnit(this.settings.general?.placeholderStyles?.placeholderPaddingTop),
      'padding-block-end': this.toUnit(this.settings.general?.placeholderStyles?.placeholderPaddingBottom),
      'padding-inline-start': this.toUnit(this.settings.general?.placeholderStyles?.placeholderPaddingLeft),
      'padding-inline-end': this.toUnit(this.settings.general?.placeholderStyles?.placeholderPaddingRight),
    };
  }

  public get containerStyle(): Record<string, string | undefined> {
    return this.commonBackgroundColor;
  }

  public get commonBackgroundColor(): Record<string, string | undefined> {
    return {
      'background-color': this.settings.general?.placeholderStyles?.inputBgColor ?? '#ffffff00'
    };
  }

  public get paddingTextStyles(): Record<string, string | undefined> {
    return this.getPaddingStyles('text');
  }

  public get isFormReady(): boolean {
    return !!this.form.value.agree || !!this.settings?.general?.legalErrorMessage?.trim();
  }

  public get isBlockButton(): boolean {
    return this.settings.general?.buttonSubmitStyles?.position === 'block';
  }

  public get buttonType(): 'icon' | 'text' {
    return this.settings.general?.buttonSubmitStyles?.type ?? 'icon';
  }

  public get hasCustomIconButton(): boolean {
    return !!this.piece?.settings?.general?.customIconButton?.image;
  }

  public get linkedCategories(): PieceNewsletterLinkedCategory[] {
    return this.piece?.settings?.general?.categories?.linkedCategories ?? [];
  }

  public get requiredCategoriesMessage(): string {
    return this.settings.general.categories?.requiredMessage ?? '';
  }

  public get hasLinks(): boolean {
    return !!this.settings.general?.links?.legalConditionsToggle
      || !!this.settings.general?.links?.unsubscribeToggle;
  }

  public get isRequiredCategories(): boolean {
    return !!this.settings.general.categories?.isRequiredToggle
      && !!this.settings.general.categories.linkedCategories.length
      && !this.form.value.linkedCategories?.some(isChecked => !!isChecked);
  }

  public get messageMandatoryBirthdate(): string {
    return this.piece?.settings?.general?.birthdate?.requiredMessage ?? '';
  }

  public get fieldsBirthdate(): PieceNewsletterFieldsBirthdate | undefined {
    return this.piece?.settings?.general?.birthdate?.fieldsBirthdate;
  }

  public get isFormComplete() {
    return this.isFormSent && !this.isLoading;
  }

  public get hasToShowTitle() {
    return this.hasToShowConfirmationTitle() || this.hasToShowFormTitle() || this.hasToShowUnsubscribeTitle();
  }

  public get titleContent() {
    if (this.hasToShowConfirmationTitle()) {
      return this.getTextByDevice(this.piece?.settings?.general?.subscriptionConfirmation, 'confirmationTitle');
    }
    if (this.hasToShowFormTitle()) {
      return this.getTextByDevice(this.piece?.settings?.general, 'titleText');
    }

    if (this.hasToShowUnsubscribeTitle()) {
      return this.getTextByDevice(this.piece?.settings?.general?.links?.unsubscribe, 'unsubscribeTitle');
    }

    return undefined;
  }

  public get titlePadding() {
    if (this.hasToShowConfirmationTitle()) {
      return this.getPaddingStyles('confirmationTitle', true, this.piece?.settings?.general?.subscriptionConfirmation);
    }
    if (this.hasToShowFormTitle()) {
      return this.getPaddingStyles('titleText', true);
    }

    if (this.hasToShowUnsubscribeTitle()) {
      return this.getPaddingStyles('unsubscribeTitle', true, this.piece?.settings?.general?.links?.unsubscribe);
    }

    return undefined;
  }

  public get hasToShowSubtitle() {
    return this.hasToShowConfirmationSubtitle() || this.hasToShowFormSubtitle() || this.hasToShowUnsubscribeSubtitle();
  }

  public get subtitleContent() {
    if (this.hasToShowConfirmationSubtitle()) {
      return this.getTextByDevice(this.piece?.settings?.general?.subscriptionConfirmation, 'confirmationSubTitle');
    }
    if (this.hasToShowFormSubtitle()) {
      return this.getTextByDevice(this.piece?.settings?.general, 'subtitleText');
    }

    if (this.hasToShowUnsubscribeSubtitle()) {
      return this.getTextByDevice(this.piece?.settings?.general?.links?.unsubscribe, 'unsubscribeSubtitle');
    }
    return undefined;
  }

  public get subtitlePadding() {
    if (this.hasToShowConfirmationSubtitle()) {
      return this.getPaddingStyles('confirmationSubTitle', true, this.piece?.settings?.general?.subscriptionConfirmation);
    }
    if (this.hasToShowFormSubtitle()) {
      return this.getPaddingStyles('subtitleText', true);
    }

    if (this.hasToShowUnsubscribeSubtitle()) {
      return this.getPaddingStyles('unsubscribeSubtitle', true, this.piece?.settings?.general?.links?.unsubscribe);
    }
    return undefined;
  }

  public get hasToShowConfirmationScreen() {
    return !this.showUnsuscribeForm && this.piece?.settings?.general.subscriptionConfirmation?.showConfirmationScreenToggle;
  }

  public get hasToShowForm() {
    return !this.hasToShowConfirmationScreen || !this.isFormComplete;
  }

  public ngOnInit(): void {
    this.form = new FormGroup({
      email: new FormControl<string | null>(null, [Validators.required, Validators.email]),
      agree: new FormControl<boolean | null>(false, [Validators.required]),
      linkedCategories: new FormArray(this.linkedCategories.map(() => new FormControl<boolean | null>(false, [Validators.required])))
    });

    this.initFormBirthdate();

    this.form.controls.birthdate?.valueChanges.pipe(
      debounceTime(300),
      takeUntil(this.clearSubscriptions$)
    ).subscribe((birthdate) => {
      if (birthdate?.day && birthdate?.day > 31) { this.form.controls.birthdate!.controls.day?.setValue(31); }
      if (birthdate?.month && birthdate?.month > 12) { this.form.controls.birthdate!.controls.month?.setValue(12); }
      if (birthdate?.year && birthdate?.year > 9999) { this.form.controls.birthdate!.controls.year?.setValue(9999); }
    });
  }

  public ngOnChanges(): void {
    if (this.form && this.type === 'workspace') {
      this.initFormBirthdate();
    }
  }

  public getPaddingStyles(prefix: string, byDevice?: boolean, object: object = this.settings.general) {
    let byDeviceString = '';
    if (byDevice) {
      byDeviceString = this.renderByDeviceService.isMobile ? 'Mobile' : 'Desktop';
    }

    return {
      'padding-block-start': this.toUnit(object?.[`${prefix}Padding${byDeviceString}Top` as keyof typeof object]),
      'padding-block-end': this.toUnit(object?.[`${prefix}Padding${byDeviceString}Bottom` as keyof typeof object]),
      'padding-inline-start': this.toUnit(object?.[`${prefix}Padding${byDeviceString}Left` as keyof typeof object]),
      'padding-inline-end': this.toUnit(object?.[`${prefix}Padding${byDeviceString}Right` as keyof typeof object]),
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public getTextByDevice(props: any, fieldText: string): string {
    if (!props) {
      return '';
    }
    if (props.enableByDevice && this.renderByDeviceService.isMobile) {
      return props[`${fieldText}Mobile`];
    }
    return props[fieldText];
  }

  public getCustomIcon(iconUrl: string): string | undefined {
    const assetsUrl = this.renderConfigurationService.config.assetsUrl;
    const akamaiOptions: AmandaOptions = { isWidthParamEnabled: false };

    return this.assetsSharedService.getAssetUrl(iconUrl, assetsUrl, akamaiOptions);
  }

  public resetFormSubmitted(): void {
    this.isFormSubmitted = false;
    this.isFormSent = false;
  }

  public onSubmit(): void {
    this.isFormSubmitted = true;

    if (this.showUnsuscribeForm && this.form.controls.email.valid) {
      this.unsusbcribe();
    } else if (this.form.valid) {
      this.susbcribe();
    }
  }

  public trackByIndex(index: number): number {
    return index;
  }


  private hasToShowConfirmationTitle() {
    return !this.showUnsuscribeForm && this.piece?.settings?.general?.subscriptionConfirmation?.showConfirmationTitleToggle && this.isFormComplete;
  }

  private hasToShowConfirmationSubtitle() {
    return !this.showUnsuscribeForm && this.piece?.settings?.general?.subscriptionConfirmation?.showConfirmationSubtitleToggle && this.isFormComplete;
  }

  private hasToShowFormTitle(): boolean | undefined {
    return !this.showUnsuscribeForm && this.piece?.settings?.general?.showTitleToggle && !this.hasToShowConfirmationTitle();
  }

  private hasToShowFormSubtitle(): boolean | undefined {
    return !this.showUnsuscribeForm && this.piece?.settings?.general?.showSubtitleToggle && !this.hasToShowConfirmationSubtitle();
  }

  private hasToShowUnsubscribeTitle(): boolean | undefined {
    return this.showUnsuscribeForm && this.piece?.settings?.general?.links?.unsubscribe?.showUnsubscribeTitleToggle && !this.isFormComplete;
  }

  private hasToShowUnsubscribeSubtitle(): boolean | undefined {
    return this.showUnsuscribeForm && this.piece?.settings?.general?.links?.unsubscribe?.showUnsubscribeSubtitleToggle && !this.isFormComplete;
  }

  private initFormBirthdate() {
    if (this.fieldsBirthdate) {
      this.form.addControl('birthdate', new FormGroup({
        day: new FormControl<number | null>(null, [Validators.maxLength(2)]),
        month: new FormControl<number | null >(null, [Validators.maxLength(2)])
      }));
      if (this.fieldsBirthdate.formatBirthdate === 'dayMonthYear') {
        (this.form.controls.birthdate as FormGroup).addControl('year', new FormControl<number | null>(null, [Validators.maxLength(4)]),);
      }
      if (this.messageMandatoryBirthdate) {
        this.form.controls.birthdate!.controls.day?.addValidators([Validators.required]);
        this.form.controls.birthdate!.controls.month?.addValidators([Validators.required]);
        this.form.controls.birthdate!.controls.year?.addValidators([Validators.required]);
      } else {
        this.form.controls.birthdate!.controls.day?.removeValidators([Validators.required]);
        this.form.controls.birthdate!.controls.month?.removeValidators([Validators.required]);
        this.form.controls.birthdate!.controls.year?.removeValidators([Validators.required]);
      }
    }
  }

  private toUnit(value: number | string | undefined): string | undefined {
    return (typeof value === 'number') ? `${value}%` : value;
  }

  private susbcribe(): void {
    this.isLoading = true;
    this.isFormSent = true;
    this.emitSubscribeNewsletterEvent();

    timer(3000).subscribe(() => {
      this.isLoading = false;
      this.cd.markForCheck();
    });
  }

  private unsusbcribe(): void {
    this.isLoading = true;
    this.isFormSent = true;
    this.emitUnsubscribeNewsletterEvent();

    timer(3000).subscribe(() => {
      this.isLoading = false;
      this.showUnsuscribeMessage = true;
      this.cd.markForCheck();
    });
  }

  private emitSubscribeNewsletterEvent(): void {
    const event = new Event('subscribeNewsletter');
    const renderEvent: RenderEvent = {
      event,
      extra: { type: 'subscribeNewsletter' },
      location: null,
      data: {
        email: this.form.value.email,
        linkedCategories: this.form.value.linkedCategories
          ?.reduce((acc, isChecked, i) => (isChecked ? [...acc, this.linkedCategories[i].id] : acc), [] as string[]),
        birthdate: this.form.value.birthdate
      }
    };
    this.renderEventsService.events.next(renderEvent);
  }

  private emitUnsubscribeNewsletterEvent(): void {
    const event = new Event('unsubscribeNewsletter');
    const renderEvent: RenderEvent = {
      event,
      extra: { type: 'unsubscribeNewsletter' },
      location: null,
      data: { email: this.form.value.email }
    };
    this.renderEventsService.events.next(renderEvent);
  }

}
